import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserRegistrationData } from '@shared/models/user.model';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { EnvironmentService } from '@app/core/services/environment.service';
import { isSafeRedirectUrl } from '@app/shared/utils/redirect-validation';

@Injectable({
	providedIn: 'root',
})
export class ClaimsService {
	private readonly http = inject(HttpClient);
	private readonly environmentService = inject(EnvironmentService);

	// Global user registration data state
	private readonly userRegistrationDataSubject = new BehaviorSubject<UserRegistrationData | null>(null);
	public userRegistrationData$ = this.userRegistrationDataSubject.asObservable();

	/**
	 * Get current user's registration data by UUID in header
	 * Always fetches fresh data from API to ensure consistency
	 * @returns Observable of UserRegistrationData or null if not found
	 */
	private getCurrentUserRegistrationData(): Observable<UserRegistrationData | null> {
		// The UUID will be attached by the AuthInterceptor automatically
		return this.http.get<UserRegistrationData>(`${this.environmentService.apiUrl}/user-registration/me`).pipe(
			map((response) => {
				return response || null;
			}),
			catchError((error) => {
				// 404 is a normal case — user simply hasn't completed registration yet
				if (error.status === 404) {
					if (!this.environmentService.isProduction) {
						console.warn('User registration data not found (404) - user may need to complete registration');
					}
					return of(null);
				}
				// For other errors, log and treat as no data
				if (!this.environmentService.isProduction) {
					console.error('Error retrieving current user registration data:', error);
				}
				return of(null);
			}),
		);
	}

	/**
	 * Initialize user registration data - now called explicitly after authentication
	 * This method can be called by AuthGuard or other components when appropriate
	 * Always fetches fresh data from API and updates global state
	 */
	public initializeUserRegistrationData(): void {
		this.getCurrentUserRegistrationData().subscribe({
			next: (data) => {
				this.userRegistrationDataSubject.next(data);
				if (!data) {
					if (!this.environmentService.isProduction) {
						console.warn('No user registration data found - user may need to complete registration');
					}
					// Redirect to external registration if no data found
					this.redirectToUserRegistration();
				}
			},
			error: (error) => {
				if (!this.environmentService.isProduction) {
					console.error('Failed to fetch user registration data on initialization:', error);
				}
				this.userRegistrationDataSubject.next(null);
				// Redirect to external registration on error as well
				this.redirectToUserRegistration();
			},
		});
	}

	/**
	 * Remove redirectUrl from browser URL if it is not whitelisted
	 * This guarantees the URL bar is actually updated
	 */
	private sanitizeRedirectUrl(): void {
		const url = new URL(window.location.href);
		const redirectUrl = url.searchParams.get('redirectUrl');

		if (!redirectUrl) return;

		if (!isSafeRedirectUrl(redirectUrl)) {
			url.searchParams.delete('redirectUrl');
			const newUrl = url.pathname + (url.search ? `?${url.searchParams.toString()}` : '');
			if (!this.environmentService.isProduction) {
				console.log('[ClaimsService] Removing unsafe redirectUrl and reloading URL:', newUrl);
			}
			window.location.replace(newUrl);
		}
	}

	/**
	 * Check if currently on the registration page
	 * Cleans redirectUrl if unsafe
	 */
	private isOnRegistrationPage(): boolean {
		const registrationPath = this.environmentService.secondRegistrationForm;

		// Sanitize redirectUrl first
		this.sanitizeRedirectUrl();

		// Check current path
		const currentUrl = new URL(window.location.href);
		const currentPath = currentUrl.origin + currentUrl.pathname;
		return currentPath === registrationPath;
	}

	/**
	 * Redirect to internal URL for user registration
	 * Uses sanitized redirectUrl
	 */
	private redirectToUserRegistration(): void {
		const registrationPath = this.environmentService.secondRegistrationForm;

		// Sanitize first to ensure only safe redirectUrl remains
		this.sanitizeRedirectUrl();

		const currentUrl = new URL(window.location.href);
		const redirectUrl = currentUrl.searchParams.get('redirectUrl');

		const finalUrl = new URL(registrationPath, currentUrl.origin);
		if (redirectUrl) finalUrl.searchParams.set('redirectUrl', redirectUrl);

		if (!this.environmentService.isProduction) {
			console.log('[ClaimsService] redirectToUserRegistration, final URL:', finalUrl.toString());
		}
		window.location.href = finalUrl.toString();
	}

	/**
	 * Check if user registration data is required and redirect if missing
	 * Always fetches fresh data from API and updates global state
	 * @returns Observable<boolean> - true if data is available, false if redirecting
	 */
	ensureUserRegistrationData(): Observable<boolean> {
		// Skip check if already on registration page to prevent redirect loop
		if (this.isOnRegistrationPage()) {
			return of(true);
		}
		// Always fetch fresh data from API to ensure consistency
		return this.getCurrentUserRegistrationData().pipe(
			tap((data: UserRegistrationData | null) => this.userRegistrationDataSubject.next(data)), // Update global state
			map((data) => {
				if (data) {
					return true;
				} else {
					// No data found, redirect to external registration
					this.redirectToUserRegistration();
					return false;
				}
			}),
			catchError((error) => {
				if (!this.environmentService.isProduction) {
					console.error('Failed to fetch user registration data:', error);
				}
				this.userRegistrationDataSubject.next(null); // Clear global state on error
				// Redirect on error as well
				this.redirectToUserRegistration();
				return of(false);
			}),
		);
	}

	/**
	 * Get current user registration data from global state (no API call)
	 * @returns Current user registration data or null
	 */
	getCurrentUserRegistrationDataValue(): UserRegistrationData | null {
		return this.userRegistrationDataSubject.value;
	}

	/**
	 * Refresh user registration data from API and update global state
	 * @returns Observable<UserRegistrationData | null>
	 */
	refreshUserRegistrationData(): Observable<UserRegistrationData | null> {
		return this.getCurrentUserRegistrationData().pipe(
			tap((data: UserRegistrationData | null) => this.userRegistrationDataSubject.next(data)), // Update global state
		);
	}

	/**
	 * Clear user registration data from global state (useful for logout)
	 */
	clearUserRegistrationData(): void {
		this.userRegistrationDataSubject.next(null);
	}
}
